/**
 * Google Tag Manager – container, grindar och Consent Mode v2.
 *
 * EN regel, i rotdokumentet. Inga undantag utspridda i enskilda routes.
 *
 * Tre grindar, i den ordning de utvärderas:
 *
 * 1. Sökväg. Sidor som bär engångstoken i URL:en (/aterstall-losenord,
 *    /inbjudan) och inloggade vyer (/dashboard, /konto, /moderering,
 *    /admin, /mina-ansprak) laddar aldrig GTM. GTM:s inbyggda
 *    sidvariabler läser adressfältet direkt och går alltså förbi
 *    maskeringen i redact-url.ts — laddas containern där skickas token
 *    till Google.
 *
 * 2. Host. Bara produktionsdomänerna. Preview och lokal utveckling ska
 *    inte skicka data. Kontrollen sker i själva snutten (klientsidan),
 *    eftersom SSR-shellen inte känner till hosten.
 *
 * 3. Samtycke. Consent Mode v2 sätts till denied för ALLT innan
 *    container-skriptet laddas — ordningsberoende och går inte att
 *    rätta i efterhand. Cookiebot (aktiverad inuti GTM) äger sedan
 *    samtyckesrutan och skickar `consent update`. Appen har ingen egen
 *    cookiebanner och ingen egen Consent Mode-kod utöver defaulten nedan.
 */
import { APP_HOST, PUBLIC_HOST } from "@/lib/site-hosts";

export const GTM_CONTAINER_ID = "GTM-P4853M9B";

/** Sökvägar (och allt under dem) där GTM aldrig får laddas. */
export const GTM_BLOCKED_PREFIXES = [
  // Engångstoken i URL:en.
  "/aterstall-losenord",
  "/inbjudan",
  // Inloggade vyer med id:n och personuppgifter i sökvägen — alla noindex.
  "/dashboard",
  "/konto",
  "/moderering",
  "/admin",
  "/mina-ansprak",
] as const;

/** Hostar där GTM får laddas. Samma mönster som site-hosts. */
export const GTM_ALLOWED_HOSTS = [
  PUBLIC_HOST,
  `www.${PUBLIC_HOST}`,
  APP_HOST,
  // Bara för prestandamätning av ett lokalt produktionsbygge. Sätts aldrig i
  // Lovables bygge, så listan är oförändrad på skolvyn.se.
  ...((import.meta.env["VITE_MATNING_HOST"] as string | undefined) ? [import.meta.env["VITE_MATNING_HOST"] as string] : []),
];

export function isGtmAllowedPath(pathname: string): boolean {
  return !isPrivatePath(pathname);
}

/**
 * Privat route = engångstoken i URL:en eller inloggad vy. Exakt samma lista
 * som grindar containern vid SSR — men denna används även vid klientsidig
 * navigering, där containern redan hunnit laddas och inte kan tas bort.
 *
 * Varje sidvisning pushar `page_access` till dataLayer (se PageViewTracker),
 * så varje utlösare i GTM kan villkoras på `page_access === 'public'`.
 * En ny privat route under någon av prefixen ärver skyddet automatiskt.
 */
export function isPrivatePath(pathname: string): boolean {
  return GTM_BLOCKED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

export type PageAccess = "public" | "private";

export function pageAccess(pathname: string): PageAccess {
  return isPrivatePath(pathname) ? "private" : "public";
}

/**
 * Consent Mode v2 – default denied. MÅSTE ligga före container-snutten.
 * Definierar även window.gtag så att cookiebannern kan skicka update.
 */
export const CONSENT_DEFAULT_SNIPPET = `
window.dataLayer=window.dataLayer||[];
function gtag(){dataLayer.push(arguments);}
window.gtag=window.gtag||gtag;
gtag('consent','default',{
'analytics_storage':'denied',
'ad_storage':'denied',
'ad_user_data':'denied',
'ad_personalization':'denied',
'functionality_storage':'denied',
'personalization_storage':'denied',
'security_storage':'denied',
'wait_for_update':500
});
gtag('set','ads_data_redaction',true);
gtag('set','url_passthrough',false);
`.trim();

/** Container-snutten, host-grindad i sig själv. */
export const GTM_SNIPPET = `
(function(w,d,s,l,i){
var h=(w.location&&w.location.hostname||'').toLowerCase();
if(${JSON.stringify(GTM_ALLOWED_HOSTS)}.indexOf(h)<0)return;
w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${GTM_CONTAINER_ID}');
`.trim();

export const GTM_NOSCRIPT_SRC = `https://www.googletagmanager.com/ns.html?id=${GTM_CONTAINER_ID}`;
